import { useState, useMemo, useCallback, useRef, type ReactNode } from 'react'
import { PluginContext } from './context'
import type { CatalogPlugin, InstalledPlugin } from './types'
import { invalidateCache } from '../../shared/api/base'
import * as pluginApi from './api'

interface PluginProviderProps {
  children: ReactNode
}

export function PluginProvider({ children }: PluginProviderProps) {
  const [catalog, setCatalog] = useState<CatalogPlugin[]>([])
  const [installed, setInstalled] = useState<InstalledPlugin[]>([])
  const [loading, setLoading] = useState(true)
  const fetchedRef = useRef(false)

  const fetchData = useCallback(async () => {
    try {
      const [catalogRes, installedRes] = await Promise.all([
        pluginApi.getCatalog(),
        pluginApi.getInstalled(),
      ])
      setCatalog(catalogRes.plugins)
      setInstalled(installedRes.plugins)
    } catch (err) {
      console.error('Failed to load plugins:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  /** Fetch plugin data lazily — only on first call */
  const fetchIfNeeded = useCallback(async () => {
    if (fetchedRef.current) return
    fetchedRef.current = true
    await fetchData()
  }, [fetchData])

  const togglePlugin = useCallback(async (pluginId: string, enabled: boolean) => {
    // Optimistic update
    setInstalled(prev => prev.map(p => p.id === pluginId ? { ...p, enabled } : p))
    setCatalog(prev => prev.map(p => p.id === pluginId ? { ...p, enabled } : p))
    try {
      await pluginApi.togglePlugin(pluginId, enabled)
      invalidateCache('/plugins')
      invalidateCache('/plugins/installed')
    } catch (err) {
      console.error('Failed to toggle plugin:', err)
      // Revert
      setInstalled(prev => prev.map(p => p.id === pluginId ? { ...p, enabled: !enabled } : p))
      setCatalog(prev => prev.map(p => p.id === pluginId ? { ...p, enabled: !enabled } : p))
    }
  }, [])

  const installPlugin = useCallback(async (pluginId: string) => {
    try {
      await pluginApi.installPlugin(pluginId)
      invalidateCache('/plugins')
      invalidateCache('/plugins/installed')
      // Refresh to get full installed plugin data
      await fetchData()
    } catch (err) {
      console.error('Failed to install plugin:', err)
    }
  }, [fetchData])

  const uninstallPlugin = useCallback(async (pluginId: string) => {
    // Optimistic: remove from installed, mark as not installed in catalog
    setInstalled(prev => prev.filter(p => p.id !== pluginId))
    setCatalog(prev => prev.map(p => p.id === pluginId ? { ...p, installed: false, enabled: false } : p))
    try {
      await pluginApi.uninstallPlugin(pluginId)
      invalidateCache('/plugins')
      invalidateCache('/plugins/installed')
    } catch (err) {
      console.error('Failed to uninstall plugin:', err)
      await fetchData() // Revert by refreshing
    }
  }, [fetchData])

  const updateSettings = useCallback(async (pluginId: string, settings: Record<string, unknown>) => {
    const res = await pluginApi.updatePluginSettings(pluginId, settings)
    setInstalled(prev => prev.map(p =>
      p.id === pluginId ? { ...p, settings: res.settings } : p,
    ))
    invalidateCache('/plugins/installed')
  }, [])

  const isEnabled = useCallback((pluginId: string) => {
    return installed.some(p => p.id === pluginId && p.enabled)
  }, [installed])

  const value = useMemo(() => ({
    catalog,
    installed,
    loading,
    togglePlugin,
    installPlugin,
    uninstallPlugin,
    updateSettings,
    isEnabled,
    refresh: fetchData,
    fetchIfNeeded,
  }), [catalog, installed, loading, togglePlugin, installPlugin, uninstallPlugin, updateSettings, isEnabled, fetchData, fetchIfNeeded])

  return (
    <PluginContext.Provider value={value}>
      {children}
    </PluginContext.Provider>
  )
}
